import { useEffect } from 'react';
import {
  FormikErrors,
  FormikTouched,
  FormikState,
  useFormikContext,
} from 'formik';
import styled from 'styled-components';
import { ProgressDots } from '@spotify-internal/encore-web';
import { useT } from '@spotify-internal/i18n-core';
import { FETCH_STATUS } from '@hooks/useSWRFetch/types';
import { useUserEmail } from '@hooks/useUserEmail';
import { formBodyHeight } from './constants';
import { ErrorState } from './ErrorState';
import { RelocationForm } from './RelocationForm';
import { SuccessState } from './SuccessState';
import { FormValues } from './types';

const LoadingIndicatorContainer = styled.div`
  display: flex;
  min-height: ${formBodyHeight};
  justify-content: center;
  align-items: center;
`;

// Import ParsedErrors type
interface ParsedErrors {
  general?: string;
  fromArtistURI?: string;
  toArtistURI?: string;
  artistURI?: string;
}

interface FormBodyProps {
  status: FETCH_STATUS;
  apiErrors: ParsedErrors;
  clearFieldError: (fieldName?: string) => void;
  values: FormValues;
  handleChange: (e: React.ChangeEvent<any>) => void;
  handleBlur: (e: any) => void;
  errors: FormikErrors<FormValues>;
  touched: FormikTouched<FormValues>;
  firstBtnRef: React.MutableRefObject<HTMLElement | null>;
}

export const FormBody = ({
  status,
  apiErrors,
  clearFieldError,
  values,
  handleChange,
  handleBlur,
  errors,
  touched,
  firstBtnRef,
}: FormBodyProps) => {
  const t = useT();

  const isLoading = status === FETCH_STATUS.FETCHING;
  const isFetched = status === FETCH_STATUS.SUCCESS;

  const { data: user, error: emailError } = useUserEmail();
  const { setFieldValue } = useFormikContext<FormikState<FormValues>>();

  useEffect(() => {
    if (user) {
      setFieldValue('email', user?.email);
    }
  }, [user, setFieldValue]);

  if (isLoading) {
    return (
      <LoadingIndicatorContainer>
      <ProgressDots />
      </LoadingIndicatorContainer>
    );
  }
  if (emailError) {
    return (
      <ErrorState
        title={t(
          'atlas.errors.error_loading_form',
          'Error loading form',
          'Error state after failed form loading',
        )}
        text={t(
          'atlas.errors.error_loading_form_try_again',
          'There was a problem loading the form. Please try reloading or reach out to <a href="mailto:content-ops+ingestion@spotify.com">Content Ops</a>.',
          '"Content Ops" should not be translated and should be kept in English because it refers to a name of a Spotify team.',
        )}
        textHasLink
      />
    );
  }
  // Note: Removed the full-page error state for API errors
  // API errors are now displayed inline in the form using FormHelpText
  if (!isLoading && isFetched) {
    return (
      <SuccessState
        title={t(
          'atlas.success.form_submitted',
          'Form submitted',
          'Confirmation that form was succesfully submitted',
        )}
        text={t(
          'atlas.success.thanks_for_reaching_out_watch_inbox',
          'Thanks for reaching out. Watch your inbox for a response from us.',
          'Indicates to user that we will reach out to them via email',
        )}
      />
    );
  }
  return (
    <RelocationForm
      firstBtnRef={firstBtnRef}
      apiErrors={apiErrors}
      clearFieldError={clearFieldError}
      values={values}
      handleChange={handleChange}
      handleBlur={handleBlur}
      errors={errors}
      touched={touched}
    />
  );
};
